import { mdiTrayArrowDown } from '@mdi/js';
import Icon from '@mdi/react';
import { Box, Button, Grid } from '@mui/material';
import { PropsWithChildren } from 'react';

import { Colors } from '../../shared/colors';

interface Props {
  onClear: () => void;
  onSubmit: () => void;
  onExportCsv?: () => void;
  exportDisabled?: boolean;
}

const outlinedButtonSx = {
  borderColor: Colors.secondary,
  borderRadius: '8px',
  color: Colors.secondary,
  fontSize: 15,
  fontWeight: 500,
  height: 48,
  textTransform: 'none',
  '&:hover': { borderColor: Colors.secondary, bgcolor: Colors.primary.light }
};

const FilterFormLayout = ({
  children,
  onClear,
  onSubmit,
  onExportCsv,
  exportDisabled = false
}: PropsWithChildren<Props>) => (
  <Box
    display="flex"
    flexDirection="column"
    flex={1}
    minHeight={0}
    sx={{
      '& .MuiInputLabel-root, & .MuiFormLabel-root': {
        color: Colors.text.label,
        fontSize: 15,
        fontWeight: 500,
        lineHeight: 1.3
      },
      '& .MuiOutlinedInput-root': {
        borderRadius: '8px',
        color: Colors.text.primary,
        fontSize: 16,
        height: 48,
        '& .MuiOutlinedInput-notchedOutline': { borderColor: Colors.border.input },
        '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: Colors.border.inputHover },
        '&.Mui-focused': { boxShadow: `0 0 0 2px ${Colors.primary.focusRing}` },
        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
          borderColor: Colors.primary.main,
          borderWidth: 1
        }
      },
      '& .MuiOutlinedInput-input': {
        color: Colors.text.primary,
        fontSize: 16,
        padding: '12px 16px',
        '&::placeholder': { color: Colors.text.placeholder, opacity: 1 }
      },
      '& .MuiAutocomplete-inputRoot .MuiAutocomplete-input': { padding: '2px 4px' },
      '& .MuiAutocomplete-popupIndicator svg, & .MuiInputAdornment-root .MuiIconButton-root svg': {
        color: Colors.primary.main
      }
    }}
  >
    <Box flex={1} minHeight={0} overflow="auto" px={{ xs: 3, sm: 4 }} pt={4} pb={4}>
      <Grid container columnSpacing={4} rowSpacing={3}>
        {children}
      </Grid>
    </Box>
    <Box
      display="grid"
      gridTemplateColumns={{ xs: '1fr', sm: onExportCsv ? 'repeat(3, minmax(0, 1fr))' : '1fr 1fr' }}
      gap={2}
      px={{ xs: 3, sm: 4 }}
      py={3}
      bgcolor={Colors.white}
      borderTop={`1px solid ${Colors.border.divider}`}
      flexShrink={0}
    >
      {onExportCsv && (
        <Button
          fullWidth
          variant="outlined"
          disabled={exportDisabled}
          startIcon={<Icon path={mdiTrayArrowDown} size={0.8} />}
          onClick={onExportCsv}
          sx={outlinedButtonSx}
        >
          Exportar CSV
        </Button>
      )}
      <Button fullWidth variant="outlined" onClick={onClear} sx={outlinedButtonSx}>
        Limpar filtros
      </Button>
      <Button
        fullWidth
        variant="contained"
        onClick={onSubmit}
        sx={{
          bgcolor: Colors.secondary,
          borderRadius: '8px',
          boxShadow: 'none',
          fontSize: 15,
          fontWeight: 500,
          height: 48,
          textTransform: 'none',
          '&:hover': { bgcolor: Colors.secondary, boxShadow: 'none' }
        }}
      >
        Filtrar
      </Button>
    </Box>
  </Box>
);

export default FilterFormLayout;
